/**
 * Session merge domain types — wire contract from oscerai/ml-scribe#10072.
 * network-sourced; do not persist outside React Query.
 *
 * TODO: Remove manual type definitions once schema is regenerated via `pnpm run schema:dev`
 * after oscerai/ml-scribe#10072 is merged.
 */

export type SessionMergeStatus = 'merged' | 'unmerged';

/** Default `custom_days` lookback — shared by the settings form and the tab subtitle. */
export const DEFAULT_SESSION_MERGE_LOOKBACK_DAYS = 7;

/**
 * Minimum merge-group member count (merged or unmerged) for the Session Merge
 * tab to show. APP-8080: a group with fewer members (0 or 1) is treated as
 * having no active merge in the UI. Shared by `useStaticTabs`, `useStaticItems`
 * and the tab coachmark gate (`useOpenSessionHasMergeTab`) so they can't drift.
 */
export const MIN_SESSION_MERGE_TAB_MEMBERS = 2;

/** Maximum `custom_days` lookback — caps the custom session-merge window at one week. */
export const MAX_SESSION_MERGE_LOOKBACK_DAYS = 7;

export interface SessionMergeMember {
  session_id: string;
  link_status: SessionMergeStatus;
  created_time_local: string | null;
  created_at: string | null;
  session_gist: string | null;
  session_name?: string | null;
}

export interface SessionMergeGroup {
  members: SessionMergeMember[];
}

/**
 * Response of `GET /sessions/{sessionId}/merged-sessions`. The backend nests the
 * candidates under a `SessionMergeGroup` object (`{ members: [...] }`), NOT a bare
 * array — read `mergeable_sessions.members`. Members carry `link_status`.
 */
export interface MergeableSessionsResponse {
  mergeable_sessions: SessionMergeGroup;
}

/** Members currently merged into the group (`link_status === 'merged'`). */
export function getMergedMembers(
  group: SessionMergeGroup | undefined
): SessionMergeMember[] {
  return group?.members?.filter((member) => member.link_status === 'merged') ?? [];
}

/** Creation instant for a merge member — local time preferred, else the UTC `created_at`. */
export const sessionMergeMemberCreatedTime = (member: SessionMergeMember): string =>
  member.created_time_local ?? member.created_at ?? '';

const sessionMergeMemberCreatedMs = (member: SessionMergeMember): number => {
  const ms = new Date(sessionMergeMemberCreatedTime(member)).getTime();
  // Undated / unparseable members collapse to epoch 0 — older than any real
  // timestamp — so under the most-recent-first order below they sort last.
  return Number.isNaN(ms) ? 0 : ms;
};

/**
 * Comparator ordering merge-group members most-recent first, with undated members
 * last. Shared by the Session Merge tab and the merge-review dialog so both
 * surfaces list candidates in the same order.
 */
export const compareSessionMergeMembersNewestFirst = (
  a: SessionMergeMember,
  b: SessionMergeMember
): number => sessionMergeMemberCreatedMs(b) - sessionMergeMemberCreatedMs(a);
